/**
 * 3:4 cover for the campaign draft wizard.
 *
 * Drop any JPEG, PNG or WebP. A crop window frames it to 3:4, then the
 * cropped JPEG goes to the brand API as multipart.
 */
import { useEffect, useRef, useState } from "react";
import { FieldTip, FormError } from "@ui/forms";
import { fontSize, space } from "@design/index";
import { api } from "@app/shared/api";
import { CoverCrop } from "./CoverCrop";
import { campaignCoverUrl } from "@app/shared/coverUrl";
import { COVER_ACCEPT, coverFileIssue, readImageSize } from "./coverPhoto";

export interface CoverRow {
  id: string;
  filename: string;
  content_type?: string;
  scan_state?: string;
}

type Crop = { src: string; width: number; height: number };

export function CoverPhoto({
  campaignId, existing, onAttached,
}: {
  campaignId: string;
  existing?: CoverRow | null;
  onAttached?: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState(existing?.filename ?? "");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop | null>(null);

  useEffect(() => {
    setFilename(existing?.filename ?? "");
  }, [existing?.filename]);

  const cropRef = useRef<Crop | null>(null);
  cropRef.current = crop;
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);
  useEffect(() => () => {
    if (cropRef.current) URL.revokeObjectURL(cropRef.current.src);
  }, []);

  function closeCrop() {
    if (crop) URL.revokeObjectURL(crop.src);
    setCrop(null);
  }

  async function onPick(file: File | undefined) {
    if (!file || busy) return;
    setError(null);
    const issue = coverFileIssue(file);
    if (issue) {
      setError(issue);
      return;
    }
    let size: { width: number; height: number; previewUrl: string };
    try {
      size = await readImageSize(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That file could not be read as an image.");
      return;
    }
    if (crop) URL.revokeObjectURL(crop.src);
    setCrop({ src: size.previewUrl, width: size.width, height: size.height });
  }

  async function onCropped(file: File) {
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file, file.name || "cover.jpg");
      const cover = await api.postForm<{ assetId?: string; filename?: string }>(
        `/campaigns/${campaignId}/cover`, body);
      if (!cover.ok) {
        setError(cover.message || "The cover could not be uploaded. Try again.");
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(file));
      setFilename(cover.data.filename ?? file.name);
      closeCrop();
      onAttached?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: space(2) }}>
      <div
        className="cm-cover-drop"
        data-active={dragging || undefined}
        data-busy={busy || undefined}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
          setDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!busy) void onPick(e.dataTransfer.files?.[0]);
        }}
      >
        <div className="cm-cover-drop-preview" aria-hidden>
          {previewUrl || existing?.id ? (
            <img src={previewUrl || campaignCoverUrl(campaignId, existing?.id)} alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          ) : (
            <span>3:4</span>
          )}
        </div>
        <div className="cm-cover-drop-copy">
          <p className="cm-cover-drop-title">
            {dragging ? "Drop the image to crop it" : "Drop a cover photo here"}
            <FieldTip text="Any JPEG, PNG or WebP up to 8 MB. Crop it to 3:4 portrait (1200 × 1600) for discovery." />
          </p>
          {filename ? (
            <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--text)" }}>
              {busy ? "Uploading…" : `On file: ${filename}`}
            </p>
          ) : null}
          <label className="cm-cover-browse">
            <span>{busy ? "Uploading…" : filename ? "Replace from files" : "Or choose a file"}</span>
            <input
              type="file"
              accept={COVER_ACCEPT}
              disabled={busy}
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                e.currentTarget.value = "";
                void onPick(file);
              }}
            />
          </label>
        </div>
      </div>
      {error ? <FormError message={error} /> : null}
      {crop ? (
        <CoverCrop
          src={crop.src}
          width={crop.width}
          height={crop.height}
          busy={busy}
          onCancel={closeCrop}
          onConfirm={(file) => void onCropped(file)}
        />
      ) : null}
    </div>
  );
}
